import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'react-apollo'
import { Link, withRouter } from 'react-router-dom'
import getRelativeUrl from 'utils/getRelativeUrl'
import withNav from 'containers/withNav'
import styled from 'styled-components'
import { media } from 'styled-bootstrap-grid'
import posed, { PoseGroup } from 'react-pose'
import easing from 'ui/styles/easing'

import Plus from './Plus'

// Animations
const PosedContainer = posed.div({
  enter: {
    x: 0,
    opacity: 1,
    transition: {
      y: { type: 'spring', stiffness: 1000, damping: 15 },
      default: { duration: 600 }
    }
  },
  exit: {
    x: '-100%',
    opacity: 0,
    transition: { duration: 600 }
  }
})

const PosedSubNav = posed.div({
  open: {
    width: typeof window !== 'undefined' && window.innerWidth <= 992 ? '100vw' : '70vw',
    delay: 100,
    delayChildren: 200,
    staggerChildren: 50
  },
  closed: {
    width: typeof window !== 'undefined' && window.innerWidth <= 992 ? 0 : '40vw'
  },
})

const PosedSubNavItem = posed.div({
  preEnter: {
    y: 20,
    opacity: 0,
  },
  enter: {
    y: 0,
    opacity: 1,
    delay: ({ i }) => i * 50 + 150,
  },
  exit: {
    opacity: 0,
    delay: 0
  }
})

// Styled components
const Wrapper = styled.div`
  width: 100%;

  ${media.lg`
    width: 40vw;
  `}
`

const AnimatedContainer = styled(PosedContainer)`
  z-index: 5;
  position: relative;
`

const MainNav = styled.div`
  width: 100%;
  background-color: ${props => props.theme.colors[props.colorScheme].light};
  height: calc(100vh - 45px);
  z-index: 6;
  position: relative;
  transition: background-color 0.3s ${easing.easeOutCubic};
  height: 100vh;
  padding: 25vh 15px 50px 15px;
  overflow: scroll;

  ${media.lg`
    width: 40vw;
    padding: 25vh 50px 0;
  `}
`

const MainNavHider = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.theme.navHeight}px;
  background: ${props => props.theme.colors[props.colorScheme].light};
  z-index: 2;
  display: none;
  
  ${media.xs`
    display: block;
  `}
`

const MainNavItem = styled.div`
  margin: 0 0 45px;
  font-size: 32px;
  line-height: 36px;
  color: ${props => props.theme.colors.grey.lighter};
  opacity: ${props => props.subNavActive ? (props.active ? 1 : 0.5) : 1};
  transition: opacity 0.3s ${easing.easeOutCubic};
  font-weight: 300;
  margin-left: 30px;

  a {
    color: inherit;
  }

  &:hover {
    cursor: pointer;
  }

  > div {
    display: flex;
    align-items: center;

    p {
      margin: 0;
    }
  }

  ${media.xs`
    font-size: 28px;
    line-height: 32px;
    margin-bottom: 35px;
  `}

  ${media.lg`
    font-size: 36px;
    margin-left: 0;
  `}
`

const MainNavItemText = styled.div`
  display: flex;
  align-items: center;
`

const SubNav = styled(PosedSubNav)`
  width: 100%;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${props => props.theme.colors[props.colorScheme].base};
  z-index: 10;
  padding-top: 25vh;
  display: flex;
  justify-content: flex-end;
  transition: background-color 0.3s ${easing.easeOutCubic};

  ${media.lg`
    width: 40vw;
    left: 50px;
    z-index: 5;
    padding-top: 25vh;
  `}
`

const SubNavItems = styled.div`
  width: 100%;
  padding-left: 45px;
  overflow: scroll;

  ${media.lg`
    width: 30vw;
    padding-left: 0;
  `}
`

const SubNavItem = styled(PosedSubNavItem)` 
  display: flex;
  font-size: 26px;
  color: white;
  margin-bottom: 40px;
  font-weight: 300;

  a {
    color: inherit;
  }
`

const BackButton = styled.button`
  position: absolute;
  top: 100px;
  left: 15px;
  color: white;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  transition: opacity 0.3s ${easing.easeOutCubic}, transform 0.3s ${easing.easeOutCubic};
  transform: translateX(${props => props.active ? 0 : 'calc(-100% - 15px)'});
  opacity: ${props => props.active ? 1 : 0};
  appearance: none;
  line-height: normal;
  border: none;
  background: none;

  svg {
    margin-right: 15px;
  }

  ${media.lg`
    display: none;
    opacity: 0;
    transform: translateX(calc(-100% - 15px));
  `}
`

class Menu extends Component {
  static propTypes = {
    active: PropTypes.bool.isRequired
  }

  state = {
    mainNavActive: false,
    subNavActive: false,
    subNavItems: [],
    subNavIndex: null
  }

  showSubnav(index) {
    this.setState({
      subNavItems: this.props.mainNav[index].childItems,
      subNavActive: true,
      subNavIndex: index
    })
  }

  hideSubnav() {
    this.setState({
      subNavItems: [],
      subNavActive: false,
      subNavIndex: null
    })
  }

  render() {
    const {
      active,
      mainNav,
      colorScheme,
      toggleNav,
    } = this.props

    const {
      subNavActive,
      subNavItems,
      subNavIndex
    } = this.state

    return(
      <Wrapper>
        <PoseGroup>
          {active &&
            <AnimatedContainer key="animatedMain">
              <MainNav colorScheme={colorScheme}>
                <MainNavHider colorScheme={colorScheme} />
                {mainNav.map((item, index) => {
                  return(
                    <MainNavItem
                      key={item.id}
                      active={subNavIndex === index}
                      subNavActive={subNavActive}
                    >
                      {item.childItems.length ? (
                        <MainNavItemText
                          onClick={() => index === subNavIndex ? this.hideSubnav() : this.showSubnav(index)}
                          key={item.id}
                        >
                          <p>{item.label}</p>
                          <Plus
                            active={subNavIndex === index}
                            color={colorScheme}
                          />
                        </MainNavItemText>
                      ) : (
                        <Link
                          to={getRelativeUrl(item.url)}
                          onClick={() => toggleNav()}
                        >
                          {item.label}
                        </Link>
                      )}
                    </MainNavItem>
                  )
                })}
              </MainNav>
              <SubNav
                withParent={false}
                pose={subNavActive ? 'open' : 'closed'}
                colorScheme={colorScheme}
              >
                <BackButton
                  onClick={() => this.hideSubnav()}
                  active={subNavActive}
                >
                  <svg
                    width="14"
                    height="11"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="#FFF"
                      fillRule="evenodd"
                      d="M5.7858521.1378644C5.9055813.0289662 5.998301 0 6.1498774 0h.6496416c.2719103 0 .3339922.1770971.151375.3431943L1.79185627 5.03553275H13.6976536c.1965252 0 .3023464.0962484.3023464.27499542v.37894366c0 .17874702-.1058212.27499542-.3023464.27499542H1.79185627L6.950894 10.65680569C7.1335112 10.82290288 7.0714293 11 6.7993175 11H6.149676c-.1515765 0-.2575992-.04124931-.3638236-.13786445L.10234433 5.69277164c-.13645911-.12411468-.13645911-.26142916 0-.38554384L5.7858524.1378639l-3e-7 5e-7z"
                    />
                  </svg>
                  Tilbage
                </BackButton>
                <SubNavItems>
                  <PoseGroup preEnterPose="preEnter">
                    {subNavItems && subNavItems.map((item, index) => {
                      return(
                        <SubNavItem key={item.id} i={index} onClick={() => toggleNav()}>
                          <Link
                            to={getRelativeUrl(item.url)}
                            >
                            {item.label}
                          </Link>
                        </SubNavItem>
                      )
                    })}
                  </PoseGroup>
                </SubNavItems>
              </SubNav>
            </AnimatedContainer>
          }
        </PoseGroup>
      </Wrapper>
    )
  }
}

export default compose(
  withRouter,
  withNav('MAIN_NAV', 'mainNav'),
)(Menu)



// WEBPACK FOOTER //
// ./src/ui/components/Navigation/Menu.js